import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useMatches, useNavigate, useNavigationType } from 'react-router';
import { useCrumbs, viewLabel } from '../../lib/crumbs.ts';
import { navForPath } from '../../lib/nav.ts';
import { historyIdx, originFor, readOrigin, withOrigin } from '../../lib/origin.ts';
import type { Origin, View } from '../../lib/origin.ts';
import type { RouteHandle } from './AppShell.tsx';

/** The page's own title: a route's handle title, else its nav label. */
export function usePageTitle(): string {
  const { pathname } = useLocation();
  const matches = useMatches();
  const handleTitle = [...matches].reverse().map((m) => (m.handle as RouteHandle | undefined)?.title).find(Boolean);
  return handleTitle ?? navForPath(pathname)?.label ?? 'Orgus';
}

const SCROLL_KEY = 'orgus.scroll';
/** Scroll positions of the main sheet by history index, so Back lands where you were. */
const scrolls = new Map<number, number>(readScrolls());

function readScrolls(): [number, number][] {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY);
    return raw ? (JSON.parse(raw) as [number, number][]) : [];
  } catch {
    return [];
  }
}
function saveScrolls(): void {
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify([...scrolls].slice(-50)));
  } catch {
    // private mode: positions last for this tab
  }
}

/** Put the main sheet back at `top` once the page is tall enough (data may arrive a moment later). */
function restoreScroll(main: HTMLElement, top: number): () => void {
  const apply = () => {
    if (main.scrollHeight - main.clientHeight >= top) {
      main.scrollTop = top;
      return true;
    }
    return false;
  };
  if (apply()) return () => {};
  const content = main.firstElementChild;
  if (!content || typeof ResizeObserver === 'undefined') return () => {};
  const ro = new ResizeObserver(() => {
    if (apply()) ro.disconnect();
  });
  ro.observe(content);
  const stop = window.setTimeout(() => ro.disconnect(), 2000);
  return () => {
    ro.disconnect();
    window.clearTimeout(stop);
  };
}

/**
 * Remembers where each page was entered from (lib/origin.ts) and keeps the main sheet's scroll
 * per history entry: a push to another page writes the origin into the new entry's state, and
 * Back (the top bar's, the browser's, Alt+Left) restores the scroll of the entry it lands on.
 */
export function useOriginTracking(): void {
  const location = useLocation();
  const kind = useNavigationType();
  const navigate = useNavigate();
  const label = viewLabel(usePageTitle(), useCrumbs());
  const prev = useRef<View | null>(null);
  /** This entry's origin as decided below, before React Router has written it into the state. */
  const current = useRef<Origin | null>(null);
  const href = `${location.pathname}${location.search}${location.hash}`;

  // 1. A new entry: write its origin, and on Back restore its scroll.
  useLayoutEffect(() => {
    const origin = originFor(prev.current, location, kind);
    current.current = origin === 'unchanged' ? readOrigin(location.state) : origin;
    if (origin !== 'unchanged') {
      navigate(href, { replace: true, state: withOrigin(location.state, origin), preventScrollReset: true });
      return;
    }
    const main = document.getElementById('main');
    const idx = historyIdx();
    if (kind === 'POP' && main && idx !== null) {
      const top = scrolls.get(idx);
      if (top !== undefined) return restoreScroll(main, top);
    }
    return undefined;
    // The location key changes once per entry; the rest is read as it is then.
  }, [location.key]);

  // 2. The view on screen, for the next navigation to name as its origin.
  useEffect(() => {
    prev.current = { href, pathname: location.pathname, label, idx: historyIdx(), origin: current.current };
  }, [location.key, href, label]);

  // 3. The main sheet's scroll, per entry.
  useEffect(() => {
    const main = document.getElementById('main');
    if (!main) return;
    const onScroll = () => {
      const idx = historyIdx();
      if (idx !== null) scrolls.set(idx, main.scrollTop);
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', saveScrolls);
    return () => {
      main.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', saveScrolls);
      saveScrolls();
    };
  }, []);
}
